import React from 'react';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';

import { DeviceMinWidths, MediaQuery } from '../../constants/size';
import useOutsideClick from '../../hooks/useOutsideClick';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import ApplicationMenuContainer from './index';
import { TOGGLE_APP_SIDEBAR } from '../../modules/app';

const Sidebar = ({ sidebarVisible }) => {
  const sidebarRef = React.useRef();
  const windowDimensions = useWindowDimensions();
  const dispatch = useDispatch();
  const userProfile = useSelector(state => state.user.userProfile);
  const location = useLocation();
  const isBookWorkspace = /^\/books\/[^/]+/.test(location.pathname);
  const showMobileBookDrawer = Boolean(
    userProfile && isBookWorkspace && sidebarVisible
  );

  const closeSidebar = () =>
    dispatch({ type: TOGGLE_APP_SIDEBAR, showAppSidebar: false });

  useOutsideClick(sidebarRef, () => {
    if (sidebarVisible && windowDimensions.width < DeviceMinWidths.tablet) {
      // hide sidebar when clicking outside it on mobile screen dimensions
      closeSidebar();
    }
  });

  return (
    <>
      {showMobileBookDrawer && (
        <MobileBookScrim
          type="button"
          aria-label="Close book menu"
          onClick={closeSidebar}
        />
      )}
      <SidebarWrapper
        ref={sidebarRef}
        sidebarVisible={sidebarVisible}
        $hasMobileNavigation={!!userProfile}
        $showMobileBookDrawer={showMobileBookDrawer}
      >
        {showMobileBookDrawer && (
          <DrawerClose
            type="button"
            aria-label="Close book menu"
            onClick={closeSidebar}
          >
            <Icon name="close" />
          </DrawerClose>
        )}
        <ApplicationMenuContainer />
      </SidebarWrapper>
    </>
  );
}

export default Sidebar;

const SidebarWrapper = styled.div`
  background-color: black;
  width: 250px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  ${MediaQuery.phone} {
    ${({ $hasMobileNavigation, $showMobileBookDrawer, sidebarVisible }) =>
      $showMobileBookDrawer
        ? css`
            position: fixed;
            inset: 0 auto 0 0;
            z-index: 210;
            display: flex;
            width: min(322px, calc(100vw - 48px));
            min-height: 100vh;
            max-height: 100vh;
            overflow-x: hidden;
            overscroll-behavior: contain;
            background: #122c2e;
            box-shadow: 18px 0 40px rgba(8, 24, 26, 0.34);

            .br-app-menu {
              display: flex !important;
              width: 100%;
              min-height: 100%;
              margin: 0 !important;
              padding-top: 0;
              border: 0 !important;
              border-radius: 0;
              background: transparent !important;
              box-shadow: none !important;
            }

            .br-app-menu > .item {
              display: flex !important;
              min-height: 48px;
              align-items: center;
              padding: 12px 18px !important;
              border-bottom: 1px solid rgba(222, 241, 238, 0.11) !important;
              color: #eff7f5 !important;
              font-size: 14px;
              font-weight: 650;
              line-height: 1.2;
            }

            .br-app-menu > .item:nth-child(1) {
              min-height: 50px;
              padding-right: 54px !important;
              color: #d9ece8 !important;
              font-size: 13px;
            }

            .br-app-menu > .item:nth-child(2) {
              min-height: 76px;
              align-items: flex-start;
              padding: 18px !important;
              border-left: 4px solid #ef7b27 !important;
              background: #1b4243 !important;
              color: #ffffff !important;
              font-size: 15px;
              font-weight: 750;
            }

            .br-app-menu > .item:nth-child(2) span:last-child {
              display: inline-block;
              margin-top: 4px;
              color: #b8d3cf;
              font-size: 12px;
              font-weight: 500;
            }

            .br-app-menu > .item.active {
              border-left: 4px solid #7ad7ca !important;
              background: #245d5c !important;
              color: #ffffff !important;
            }

            .br-app-menu > .item.active > .br-app-icon-wrapper {
              color: #b7f0e8 !important;
            }

            .br-app-menu > nav[aria-label="Book submenu"] {
              margin: 0 !important;
              padding: 4px 0 7px;
              background: rgba(7, 26, 28, 0.25) !important;
            }

            .br-app-menu > nav[aria-label="Book submenu"] > .br-button {
              min-height: 34px;
              padding: 7px 18px 7px 39px !important;
              border: 0 !important;
              color: #c7dad6 !important;
              font-size: 12px;
              font-weight: 500;
            }

            .br-app-menu > nav[aria-label="Book submenu"] > .br-button[aria-current="page"] {
              border-left: 3px solid #7ad7ca !important;
              background: rgba(122, 215, 202, 0.14) !important;
              color: #ffffff !important;
            }

            .br-app-menu > .item.book-actions {
              display: block !important;
              margin-top: auto !important;
              padding: 12px 16px max(18px, env(safe-area-inset-bottom)) !important;
              border-top: 1px solid rgba(222, 241, 238, 0.16) !important;
              border-bottom: 0 !important;
              background: #0d2325 !important;
            }

            .br-app-menu > .item.book-actions .br-button {
              min-height: 42px;
              border-radius: 10px;
              font-weight: 700;
            }

            .br-app-menu > .item.book-actions .br-button--primary {
              background: #ef7b27 !important;
            }
          `
        : $hasMobileNavigation
          ? css`
              display: none;
            `
          : sidebarVisible
            ? css`
                position: fixed;
                top: 0;
                max-height: 100vh;
                min-height: 100vh;
                z-index: 200;
              `
            : css`
                display: none;
              `}
  }
  ${MediaQuery.tablet} {
    --offset: var(--space);
    position: sticky;
    top: var(--offset);
    width: 250px;
    max-height: calc(100vh - var(--offset) - var(--header-height));
    ${MediaQuery.tablet} {
      ${({ sidebarVisible }) => sidebarVisible ? css`
      ` : css`
        display: none;
      `}
    }
  }
`;

const MobileBookScrim = styled.button`
  display: none;

  ${MediaQuery.phone} {
    position: fixed;
    inset: 0;
    z-index: 209;
    display: block;
    width: 100%;
    border: 0;
    background: rgba(8, 24, 26, 0.46);
    cursor: default;
  }
`;

const DrawerClose = styled.button`
  display: none;

  ${MediaQuery.phone} {
    position: fixed;
    top: 7px;
    left: min(266px, calc(100vw - 104px));
    z-index: 211;
    display: grid;
    width: 36px;
    height: 36px;
    place-items: center;
    border: 0;
    border-radius: 50%;
    background: rgba(232, 247, 243, 0.12);
    color: #e8f7f3;
    cursor: pointer;

    > .br-app-icon-wrapper {
      margin: 0 !important;
    }

    &:focus-visible {
      outline: 3px solid #7ad7ca;
      outline-offset: 2px;
    }
  }
`;
